/**
 * The Academics → Assessments (templates) list page's filter + sort
 * declaration — a consumer of the generic filter toolkit
 * (`components/filters`), mirroring `studentsFilterConfig` /
 * `withdrawalsFilterConfig`.
 *
 * Facets exposed on the bar:
 *   - search    → `q`                       template-title search.
 *   - scope     → `scope`                   multi-select over hiring/rules/
 *                                            weekly_quiz/monthly_quiz/
 *                                            interview_script; `omit` encoding.
 *                                            CRUCIAL: the Games nav shortcuts
 *                                            link with `?scope=weekly_quiz` (a
 *                                            single value); the decoder splits
 *                                            it into `values: ['weekly_quiz']`,
 *                                            so the URL prefilter renders as a
 *                                            selected pill AND sends
 *                                            `scope=weekly_quiz` to the API with
 *                                            no special handling.
 *   - status    → `status`                  multi-select over draft/in_review/
 *                                            published/archived; `omit`.
 *   - mode      → `mode`                     multi-select over timed_exam/
 *                                            practice; `omit`.
 *   - ownerType → `ownerType`               multi-select over global/course/
 *                                            batch; `omit`.
 *   - created   → `createdFrom` / `createdTo` date range over the template's
 *                                            `createdAt`.
 *
 * Sorts map to the backend's allow-listed `sort` fields ({createdAt, title}).
 * The default (first entry) is "Recently created" = createdAt:desc.
 *
 * Sortable column header: Title (title) — both directions are registered so the
 * header can toggle asc↔desc without snapping back to the default sort.
 * createdAt stays dropdown-only (the table's "Updated" column shows `updatedAt`,
 * which is NOT a sort field, so it is intentionally not wired to a header).
 */

import { z } from 'zod';
import type { ListFilterConfig, SortColumnBinding } from '../filters';

export const assessmentsFilterConfig: ListFilterConfig = {
  facets: [
    {
      kind: 'search',
      key: 'q',
      label: 'Search',
      placeholder: 'Search by title',
    },
    {
      kind: 'multi-select',
      key: 'scope',
      label: 'Scope',
      group: 'filters',
      // Absent scope = all on the backend; "All" is encoded by omitting the
      // param. A single `?scope=weekly_quiz` from a nav shortcut decodes into
      // one selected value and renders as a pill.
      defaultValue: [],
      allEncoding: 'omit',
      options: [
        { value: 'hiring', label: 'Hiring' },
        { value: 'rules', label: 'Rules' },
        { value: 'weekly_quiz', label: 'Weekly quiz' },
        { value: 'monthly_quiz', label: 'Monthly quiz' },
        { value: 'interview_script', label: 'Interview script' },
      ],
    },
    {
      kind: 'multi-select',
      key: 'status',
      label: 'Status',
      group: 'filters',
      defaultValue: [],
      allEncoding: 'omit',
      options: [
        { value: 'draft', label: 'Draft' },
        { value: 'in_review', label: 'In review' },
        { value: 'published', label: 'Published' },
        { value: 'archived', label: 'Archived' },
      ],
    },
    {
      kind: 'multi-select',
      key: 'mode',
      label: 'Mode',
      group: 'filters',
      defaultValue: [],
      allEncoding: 'omit',
      options: [
        { value: 'timed_exam', label: 'Timed exam' },
        { value: 'practice', label: 'Practice' },
      ],
    },
    {
      kind: 'multi-select',
      key: 'ownerType',
      label: 'Owner',
      group: 'filters',
      defaultValue: [],
      allEncoding: 'omit',
      options: [
        { value: 'global', label: 'Global' },
        { value: 'course', label: 'Course' },
        { value: 'batch', label: 'Batch' },
      ],
    },
    {
      kind: 'date-range',
      key: 'created',
      label: 'Created',
      group: 'filters',
      params: { from: 'createdFrom', to: 'createdTo' },
    },
  ],
  sorts: [
    { value: 'createdAt:desc', label: 'Recently created' },
    { value: 'createdAt:asc', label: 'Oldest first' },
    // Both title directions registered so the Title column header can toggle.
    { value: 'title:asc', label: 'Title A–Z' },
    { value: 'title:desc', label: 'Title Z–A' },
  ],
  resultNoun: ['template', 'templates'],
};

/**
 * Column-header sort bindings, synced to the same `sort` param. Only the Title
 * column is bound: it sorts on `title`, which has both directions registered
 * above. createdAt stays dropdown-only.
 */
export const ASSESSMENTS_SORT_COLUMNS: Record<'title', SortColumnBinding> = {
  title: { field: 'title', firstClickDir: 'asc' },
};

/**
 * URL search-param contract for the Assessments list, used as the route's
 * `validateSearch`. The filter toolkit is the single source of truth for these
 * values; this schema validates the raw URL at the route boundary (everything
 * optional, since a bare `/academics/assessments` opens on the configured
 * defaults).
 *
 * EVERY param key the config can emit must appear here — `z.object().parse`
 * strips unknown keys, so a missing key would be silently dropped on the next
 * read. In particular `created` is the date-range facet's PRESET key (e.g.
 * `last7`), distinct from the concrete custom bounds `createdFrom` /
 * `createdTo`; omitting it would stop a preset choice from sticking.
 *
 * `scope` is a plain `z.string()` (not a `z.enum`) so a comma-joined
 * multi-select value survives the boundary — and so the Games nav shortcuts'
 * `?scope=weekly_quiz` single-value prefilter still validates and flows into
 * the scope facet. Kept beside the config (not in the route) so this boundary
 * is unit-testable without loading the route's React/router side effects.
 */
export const assessmentsSearchSchema = z.object({
  q: z.string().optional(),
  scope: z.string().optional(),
  status: z.string().optional(),
  mode: z.string().optional(),
  ownerType: z.string().optional(),
  created: z.string().optional(),
  createdFrom: z.string().optional(),
  createdTo: z.string().optional(),
  sort: z.string().optional(),
  page: z.coerce.number().int().min(1).optional(),
});
